import type { LocalizedArticle } from './types';

export const categoriesArticle: LocalizedArticle = {
  en: {
    seoTitle: 'USFans Product Categories Guide: Shoes to Accessories 2026',
    metaDescription:
      'Explore the full USFans product categories, from shoes and hoodies to bags, belts and perfumes, and learn how to navigate each category.',
    h1: 'USFans Product Categories Guide',
    intro:
      'The USFans spreadsheet is organized around a wide range of product categories. This guide walks through the main categories, what you can expect to find in each, and how to navigate them before you continue to the main website.',
    sections: [
      {
        heading: 'Clothing Categories',
        body: [
          'Clothing makes up a large part of the directory. Common categories include shoes, hoodies and sweaters, t-shirts, jackets, and pants and shorts.',
          'Each category groups similar items together so you can compare options within a single product type instead of searching everywhere.',
        ],
      },
      {
        heading: 'Sportswear and Sets',
        body: [
          'Categories such as jerseys and sets focus on sportswear and coordinated outfits. Jerseys often include team-style designs, while sets pair matching tops and bottoms.',
          'Headwear such as caps is also available for those looking to complete an outfit with the right accessory.',
        ],
      },
      {
        heading: 'Accessories and Essentials',
        body: [
          'Beyond clothing, the directory covers accessories, bags, belts and perfumes. These categories are useful when you want a smaller or complementary item.',
          'There is also an underwear and underpants category for everyday essentials, plus an other category for anything that does not fit elsewhere.',
        ],
      },
      {
        heading: 'How to Navigate the Categories',
        body: [
          'Use the category directory on this page to jump directly to any category. Each link opens the matching section on the main website.',
          'Starting from a specific category makes it easier to compare products and sellers within the type you care about.',
        ],
      },
    ],
    table: {
      headers: ['Category Group', 'Examples'],
      rows: [
        ['Clothing', 'Shoes, Hoodies, T-Shirts, Jackets, Pants'],
        ['Sportswear', 'Jerseys, Sets, Headwear'],
        ['Accessories', 'Bags, Belts, Accessories, Perfumes'],
        ['Essentials', 'Underwear, Other'],
      ],
    },
    pros: [
      'Clear overview of all categories',
      'Helps you find products faster',
      'Explains category grouping',
      'Independent and neutral guidance',
    ],
    cons: [
      'Availability varies by seller',
      'Categories may change over time',
      'Not a complete product list',
    ],
    faq: [
      { q: 'What product categories are available?', a: 'Categories include shoes, hoodies, t-shirts, jackets, pants, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes and more.' },
      { q: 'How do I browse a category?', a: 'Use the category directory on this page to jump directly to the category you want on the main website.' },
      { q: 'Are all categories always available?', a: 'Availability can change, so check the current category listings on the main website.' },
      { q: 'Which category should I start with?', a: 'Start with the product type you are most interested in, such as shoes or hoodies, then browse from there.' },
    ],
  },
  es: {
    seoTitle: 'Guía de Categorías de Productos USFans 2026',
    metaDescription:
      'Explora todas las categorías de productos de USFans, desde zapatos y sudaderas hasta bolsos, cinturones y perfumes, y aprende a navegar cada categoría.',
    h1: 'Guía de Categorías de Productos USFans',
    intro:
      'La USFans spreadsheet está organizada en torno a una amplia variedad de categorías de productos. Esta guía recorre las principales categorías, qué puedes esperar encontrar en cada una y cómo navegarlas antes de continuar al sitio web principal.',
    sections: [
      {
        heading: 'Categorías de Ropa',
        body: [
          'La ropa constituye gran parte del directorio. Las categorías comunes incluyen zapatos, sudaderas y jerséis, camisetas, chaquetas, y pantalones y pantalones cortos.',
          'Cada categoría agrupa artículos similares para que compares opciones dentro de un mismo tipo de producto en lugar de buscar en todas partes.',
        ],
      },
      {
        heading: 'Ropa Deportiva y Conjuntos',
        body: [
          'Categorías como camisetas deportivas y conjuntos se centran en ropa deportiva y atuendos coordinados. Las camisetas suelen incluir diseños de equipo, mientras que los conjuntos combinan parte superior e inferior.',
          'Los gorros también están disponibles para quienes quieren completar un atuendo con el accesorio adecuado.',
        ],
      },
      {
        heading: 'Accesorios y Esenciales',
        body: [
          'Además de la ropa, el directorio cubre accesorios, bolsos, cinturones y perfumes. Estas categorías son útiles cuando buscas un artículo más pequeño o complementario.',
          'También hay una categoría de ropa interior para lo esencial del día a día, además de una categoría otros para lo que no encaja en el resto.',
        ],
      },
      {
        heading: 'Cómo Navegar las Categorías',
        body: [
          'Usa el directorio de categorías de esta página para ir directamente a cualquier categoría. Cada enlace abre la sección correspondiente en el sitio web principal.',
          'Empezar desde una categoría específica facilita comparar productos y vendedores dentro del tipo que te interesa.',
        ],
      },
    ],
    table: {
      headers: ['Grupo de Categorías', 'Ejemplos'],
      rows: [
        ['Ropa', 'Zapatos, Sudaderas, Camisetas, Chaquetas, Pantalones'],
        ['Ropa deportiva', 'Camisetas deportivas, Conjuntos, Gorros'],
        ['Accesorios', 'Bolsos, Cinturones, Accesorios, Perfumes'],
        ['Esenciales', 'Ropa interior, Otros'],
      ],
    },
    pros: [
      'Visión clara de todas las categorías',
      'Ayuda a encontrar productos más rápido',
      'Explica la agrupación de categorías',
      'Guía independiente y neutral',
    ],
    cons: [
      'La disponibilidad varía según el vendedor',
      'Las categorías pueden cambiar con el tiempo',
      'No es una lista completa de productos',
    ],
    faq: [
      { q: '¿Qué categorías de productos hay?', a: 'Incluyen zapatos, sudaderas, camisetas, chaquetas, pantalones, gorros, conjuntos, ropa interior, camisetas deportivas, accesorios, bolsos, cinturones, perfumes y más.' },
      { q: '¿Cómo navego por una categoría?', a: 'Usa el directorio de categorías de esta página para ir directamente a la categoría que quieras en el sitio principal.' },
      { q: '¿Todas las categorías están siempre disponibles?', a: 'La disponibilidad puede cambiar, así que revisa los listados actuales en el sitio principal.' },
      { q: '¿Por qué categoría empiezo?', a: 'Empieza por el tipo de producto que más te interese, como zapatos o sudaderas, y explora desde ahí.' },
    ],
  },
  fr: {
    seoTitle: 'Guide des Catégories de Produits USFans 2026',
    metaDescription:
      'Explorez toutes les catégories de produits USFans, des chaussures et sweats aux sacs, ceintures et parfums, et apprenez à naviguer dans chaque catégorie.',
    h1: 'Guide des Catégories de Produits USFans',
    intro:
      'La USFans spreadsheet est organisée autour d\'une grande variété de catégories de produits. Ce guide parcourt les principales catégories, ce que vous pouvez attendre de chacune et comment les naviguer avant de poursuivre vers le site principal.',
    sections: [
      {
        heading: 'Catégories de Vêtements',
        body: [
          'Les vêtements constituent une grande partie du répertoire. Les catégories courantes incluent les chaussures, les sweats et pulls, les t-shirts, les vestes, et les pantalons et shorts.',
          'Chaque catégorie regroupe des articles similaires pour comparer les options au sein d\'un même type de produit.',
        ],
      },
      {
        heading: 'Vêtements de Sport et Ensembles',
        body: [
          'Des catégories comme les maillots et les ensembles se concentrent sur le sport et les tenues coordonnées. Les maillots incluent souvent des designs d\'équipe, tandis que les ensembles associent haut et bas.',
          'Les couvre-chefs comme les casquettes sont aussi disponibles pour compléter une tenue.',
        ],
      },
      {
        heading: 'Accessoires et Essentiels',
        body: [
          'Au-delà des vêtements, le répertoire couvre les accessoires, les sacs, les ceintures et les parfums. Ces catégories sont utiles pour un article plus petit ou complémentaire.',
          'Il existe aussi une catégorie sous-vêtements pour les essentiels du quotidien, plus une catégorie autres pour ce qui ne rentre pas ailleurs.',
        ],
      },
      {
        heading: 'Comment Naviguer dans les Catégories',
        body: [
          'Utilisez le répertoire des catégories de cette page pour accéder directement à n\'importe quelle catégorie. Chaque lien ouvre la section correspondante sur le site principal.',
          'Commencer par une catégorie précise facilite la comparaison des produits et vendeurs dans le type qui vous intéresse.',
        ],
      },
    ],
    table: {
      headers: ['Groupe de Catégories', 'Exemples'],
      rows: [
        ['Vêtements', 'Chaussures, Sweats, T-shirts, Vestes, Pantalons'],
        ['Sport', 'Maillots, Ensembles, Couvre-chefs'],
        ['Accessoires', 'Sacs, Ceintures, Accessoires, Parfums'],
        ['Essentiels', 'Sous-vêtements, Autres'],
      ],
    },
    pros: [
      'Aperçu clair de toutes les catégories',
      'Aide à trouver les produits plus vite',
      'Explique le regroupement des catégories',
      'Guide indépendant et neutre',
    ],
    cons: [
      'La disponibilité varie selon le vendeur',
      'Les catégories peuvent changer',
      'Pas une liste complète de produits',
    ],
    faq: [
      { q: 'Quelles catégories de produits sont disponibles ?', a: 'Chaussures, sweats, t-shirts, vestes, pantalons, couvre-chefs, ensembles, sous-vêtements, maillots, accessoires, sacs, ceintures, parfums et plus.' },
      { q: 'Comment parcourir une catégorie ?', a: 'Utilisez le répertoire des catégories de cette page pour accéder directement à la catégorie souhaitée sur le site principal.' },
      { q: 'Toutes les catégories sont-elles toujours disponibles ?', a: 'La disponibilité peut changer, alors consultez les listes actuelles sur le site principal.' },
      { q: 'Par quelle catégorie commencer ?', a: 'Commencez par le type de produit qui vous intéresse le plus, comme les chaussures ou les sweats, puis explorez à partir de là.' },
    ],
  },
  pl: {
    seoTitle: 'Przewodnik po Kategoriach Produktów USFans 2026',
    metaDescription:
      'Poznaj wszystkie kategorie produktów USFans, od butów i bluz po torby, paski i perfumy, oraz naucz się poruszać po każdej kategorii.',
    h1: 'Przewodnik po Kategoriach Produktów USFans',
    intro:
      'USFans spreadsheet jest zorganizowany wokół szerokiej gamy kategorii produktów. Ten przewodnik omawia główne kategorie, czego można oczekiwać w każdej z nich i jak się po nich poruszać przed przejściem do głównej witryny.',
    sections: [
      {
        heading: 'Kategorie Odzieży',
        body: [
          'Odzież stanowi dużą część katalogu. Typowe kategorie to buty, bluzy i swetry, koszulki, kurtki oraz spodnie i szorty.',
          'Każda kategoria grupuje podobne przedmioty, aby można było porównać opcje w ramach jednego typu produktu.',
        ],
      },
      {
        heading: 'Odzież Sportowa i Zestawy',
        body: [
          'Kategorie takie jak koszulki sportowe i zestawy koncentrują się na odzieży sportowej i skoordynowanych strojach. Koszulki często zawierają wzory drużynowe, a zestawy łączą górę z dołem.',
          'Nakrycia głowy, takie jak czapki, są również dostępne dla osób chcących dopełnić strój.',
        ],
      },
      {
        heading: 'Akcesoria i Niezbędniki',
        body: [
          'Poza odzieżą katalog obejmuje akcesoria, torby, paski i perfumy. Te kategorie są przydatne, gdy szukasz mniejszego lub uzupełniającego przedmiotu.',
          'Jest też kategoria bielizny na co dzień oraz kategoria inne na wszystko, co nie pasuje do reszty.',
        ],
      },
      {
        heading: 'Jak Poruszać się po Kategoriach',
        body: [
          'Skorzystaj z katalogu kategorii na tej stronie, aby przejść bezpośrednio do dowolnej kategorii. Każdy link otwiera odpowiednią sekcję na głównej witrynie.',
          'Zaczynanie od konkretnej kategorii ułatwia porównywanie produktów i sprzedawców w interesującym Cię typie.',
        ],
      },
    ],
    table: {
      headers: ['Grupa Kategorii', 'Przykłady'],
      rows: [
        ['Odzież', 'Buty, Bluzy, Koszulki, Kurtki, Spodnie'],
        ['Odzież sportowa', 'Koszulki sportowe, Zestawy, Nakrycia głowy'],
        ['Akcesoria', 'Torby, Paski, Akcesoria, Perfumy'],
        ['Niezbędniki', 'Bielizna, Inne'],
      ],
    },
    pros: [
      'Jasny przegląd wszystkich kategorii',
      'Pomaga szybciej znaleźć produkty',
      'Wyjaśnia grupowanie kategorii',
      'Niezależne i neutralne wskazówki',
    ],
    cons: [
      'Dostępność różni się w zależności od sprzedawcy',
      'Kategorie mogą się zmieniać',
      'Nie jest to pełna lista produktów',
    ],
    faq: [
      { q: 'Jakie kategorie produktów są dostępne?', a: 'Buty, bluzy, koszulki, kurtki, spodnie, nakrycia głowy, zestawy, bielizna, koszulki sportowe, akcesoria, torby, paski, perfumy i inne.' },
      { q: 'Jak przeglądać kategorię?', a: 'Skorzystaj z katalogu kategorii na tej stronie, aby przejść bezpośrednio do wybranej kategorii na głównej witrynie.' },
      { q: 'Czy wszystkie kategorie są zawsze dostępne?', a: 'Dostępność może się zmieniać, więc sprawdź aktualne listy na głównej witrynie.' },
      { q: 'Od której kategorii zacząć?', a: 'Zacznij od typu produktu, który najbardziej Cię interesuje, np. butów lub bluz, a następnie eksploruj dalej.' },
    ],
  },
};